/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useContext, useState, useEffect} from 'react'
import {useLocation} from 'react-router-dom'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Flex,
    Text,
    Modal,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Stack,
    useBreakpointValue,
    ModalFooter
} from '@chakra-ui/react'
import useBasket from '../commerce-api/hooks/useBasket'
import Link from '../components/link'
import RecommendedProducts from '../components/recommended-products'
import {CartIcon, CheckIcon, LockIcon} from '../components/icons'
import Callout from '../components/content-asset'
import textStyles from '../theme/foundations/textStyles'
import useMultiSite from '../hooks/use-multi-site'
import {CART_HREF, CRO_EXPERIMENTS, CRO_LOCATION} from '../constants'
import {useCroSlot, useCroVariantActive} from '../components/cro'
import {getProductsToAddToCart} from '../utils/product-utils'

/**
 * This is the context for managing the AddToCartModal.
 * Used in top level App component.
 */
export const AddToCartModalContext = React.createContext()
export const useAddToCartModalContext = () => useContext(AddToCartModalContext)
export const AddToCartModalProvider = ({children}) => {
    const addToCartModal = useAddToCartModal()
    return (
        <AddToCartModalContext.Provider value={addToCartModal}>
            {children}
            <AddToCartModal />
        </AddToCartModalContext.Provider>
    )
}
AddToCartModalProvider.propTypes = {
    children: PropTypes.node.isRequired
}

/**
 * Visual feedback (a modal) for adding item to the cart.
 */
export const AddToCartModal = () => {
    const {isOpen, onClose, data, onRecommendedProductClick} = useAddToCartModalContext()
    const {product, itemsAdded = []} = data || {}
    const quantity = itemsAdded?.length
    const intl = useIntl()
    const basket = useBasket()
    const size = useBreakpointValue({lg: '2xl', xl: '4xl'})
    const itemsPerSlide = useBreakpointValue({base: 2, lg: 3})
    /**
     * [isCentered] is used to:
     * - Center the modal vertically on Desktop (Default - on mobile view modal is attached to bottom)
     * - [isCentered] = false ? Mobile : Desktop
     * - Hide (Cart items & Order details) on mobile view when recommendations are available
     */
    const isCentered = useBreakpointValue({base: false, lg: true})
    const {site} = useMultiSite()
    useCroSlot(CRO_LOCATION.ADD_TO_CART_MODAL, isOpen)
    const cro008VariantActive = useCroVariantActive(
        CRO_EXPERIMENTS.CRO_008_AddToCartModal.experimentId,
        CRO_EXPERIMENTS.CRO_008_AddToCartModal.variantId
    )

    if (!isOpen) {
        return null
    }
    const {currency, productSubTotal, itemAccumulatedCount} = basket

    const isCart = /\/appcart$/.test(location?.pathname)

    return (
        <Modal
            size={size}
            isOpen={isOpen}
            onClose={onClose}
            isCentered={isCentered}
            variant="cart"
            colorScheme="stokkeGray"
        >
            <ModalOverlay />
            <ModalContent containerProps={{'data-testid': 'add-to-cart-modal'}}>
                <ModalHeader>
                    {cro008VariantActive ? (
                        <>
                            <Flex alignItems="center" gap={4}>
                                <CheckIcon boxSize={4} color="stokkeFunctional.alertGreen" />
                                <Text textStyle="Regular 3XLarge">
                                    {intl.formatMessage(
                                        {
                                            defaultMessage:
                                                '{quantity} {quantity, plural, one {item} other {items}} successfully added to cart',
                                            id: 'add_to_cart_modal.info.successfully_added_to_cart'
                                        },
                                        {quantity}
                                    )}
                                </Text>
                            </Flex>
                            <Stack
                                spacing="4"
                                display={{lg: 'none'}}
                                marginTop={8}
                                marginBottom={4}
                            >
                                <Button
                                    as={Link}
                                    to={CART_HREF}
                                    width="100%"
                                    variant="solid"
                                    size="lg"
                                    rightIcon={<CartIcon />}
                                    {...textStyles['Regular Medium']}
                                >
                                    {intl.formatMessage({
                                        defaultMessage: 'View Cart',
                                        id: 'add_to_cart_modal.link.view_cart'
                                    })}
                                </Button>

                                <Callout id="pwa-addtocart-usp" />
                            </Stack>
                        </>
                    ) : (
                        <Text textStyle="Regular 2XLarge">
                            {intl.formatMessage(
                                {
                                    defaultMessage:
                                        '{quantity} {quantity, plural, one {item} other {items}} added to cart',
                                    id: 'add_to_cart_modal.info.added_to_cart'
                                },
                                {quantity}
                            )}
                        </Text>
                    )}
                </ModalHeader>
                {/* fill the space above footer on lg */}
                <Box bgColor="stokkeCore.white" />
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4} flex={1} paddingBottom={{base: 6, lg: 12}}>
                        <RecommendedProducts
                            wrapper={({children}) => (
                                <Box paddingTop="2rem" className="is-atc-recommendation">
                                    {children}
                                </Box>
                            )}
                            recommender={`recom_Basket_${site.id}`}
                            products={[product]}
                            shouldFetch={() => product?.id}
                            variation="Extended"
                            onRecommendedProductClick={onRecommendedProductClick}
                            itemsPerSlide={itemsPerSlide}
                            title={
                                <FormattedMessage
                                    defaultMessage="You Might Also Like"
                                    id="add_to_cart_modal.recommended_products.title.might_also_like"
                                />
                            }
                            variant="addToCart"
                        />
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    {isCentered && (
                        <Box>
                            <Flex justifyContent="space-between" mb={4}>
                                <Text {...textStyles['Regular Medium']}>
                                    {intl.formatMessage({
                                        defaultMessage: 'Just added',
                                        id: 'add_to_cart_modal.label.just_added'
                                    })}
                                </Text>
                                <Text alignSelf="flex-end" {...textStyles['Regular Medium']}>
                                    {product.price &&
                                        intl.formatNumber(product.price, {
                                            style: 'currency',
                                            currency: currency
                                        })}
                                </Text>
                            </Flex>
                            <Flex justifyContent="space-between">
                                <Text {...textStyles['Regular Medium']}>
                                    {intl.formatMessage(
                                        {
                                            defaultMessage:
                                                'Cart subtotal ({itemAccumulatedCount, plural, =0 {0 items} one {# item} other {# items}})',
                                            id: 'add_to_cart_modal.label.cart_subtotal'
                                        },
                                        {itemAccumulatedCount}
                                    )}
                                </Text>
                                <Text alignSelf="flex-end" {...textStyles['Regular Medium']}>
                                    {productSubTotal &&
                                        intl.formatNumber(productSubTotal, {
                                            style: 'currency',
                                            currency: currency
                                        })}
                                </Text>
                            </Flex>
                        </Box>
                    )}

                    {cro008VariantActive ? (
                        <Stack spacing="4" display={{base: 'none', lg: 'flex'}}>
                            <Button
                                as={Link}
                                to={CART_HREF}
                                width="100%"
                                variant="solid"
                                size="lg"
                                rightIcon={<CartIcon />}
                                {...textStyles['Regular Medium']}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'View Cart',
                                    id: 'add_to_cart_modal.link.view_cart'
                                })}
                            </Button>

                            <Callout id="pwa-addtocart-usp" />
                        </Stack>
                    ) : (
                        <Stack spacing="4">
                            <Button
                                as={Link}
                                to={CART_HREF}
                                onClick={() => isCart && onClose()}
                                width="100%"
                                variant="outline"
                                size="lg"
                                {...textStyles['Regular Medium']}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'View Cart',
                                    id: 'add_to_cart_modal.link.view_cart'
                                })}
                            </Button>

                            <Button
                                as={Link}
                                to="/appcheckout"
                                width="100%"
                                variant="placeOrder"
                                size="lg"
                                rightIcon={<LockIcon />}
                                {...textStyles['Regular Medium']}
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Proceed to Checkout',
                                    id: 'add_to_cart_modal.link.checkout'
                                })}
                            </Button>

                            <Callout id="pwa-addtocart-usp" />
                        </Stack>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

AddToCartModal.propTypes = {
    product: PropTypes.shape({
        name: PropTypes.string,
        imageGroups: PropTypes.array
    }),
    itemsAdded: PropTypes.array,
    variant: PropTypes.shape({
        productId: PropTypes.string,
        variationValues: PropTypes.object
    }),
    quantity: PropTypes.number,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    children: PropTypes.any
}

export const useAddToCartModal = () => {
    const [state, setState] = useState({
        isOpen: false,
        data: null
    })

    const {pathname} = useLocation()
    useEffect(() => {
        setState((state) =>
            state.isOpen
                ? {
                      ...state,
                      isOpen: false,
                      data: null
                  }
                : state
        )
    }, [pathname])

    return {
        isOpen: state.isOpen,
        data: state.data,
        onRecommendedProductClick: state.onRecommendedProductClick,
        onOpen: (onRecommendedProductClick) => {
            setState({
                ...state,
                isOpen: true,
                onRecommendedProductClick
            })
        },
        onClose: () => {
            setState({
                isOpen: false,
                data: null
            })
        },
        addProduct: (product) => {
            if (!state.data) {
                const itemsAdded = getProductsToAddToCart(product)

                setState({
                    ...state,
                    data: {
                        product,
                        itemsAdded
                    }
                })
            } else {
                setState({
                    ...state,
                    data: {...state.data, itemsAdded: [...state.data.itemsAdded, product]}
                })
            }
        }
    }
}
